export const ONBOARDING_BASE_PATH = "/onboarding"

/** History state the email step hands to the password step. */
export interface SignUpEmailState {
  email: string
}

export type QuizSection = "aboutYou" | "goals" | "learningStyle" | "plan"

export const QUIZ_SECTIONS: QuizSection[] = [
  "aboutYou",
  "goals",
  "learningStyle",
  "plan",
]

export interface QuizOption {
  emoji?: string
  id: string
  subOptions?: QuizOption[]
}

export type InfoVisual =
  | "socialProof"
  | "forgettingCurve"
  | "mindMapDemo"
  | "dailyHabit"
  | "personalized"
  | "welcome"

export type QuizStep =
  | {
      id: string
      options: QuizOption[]
      section: QuizSection
      type: "single"
    }
  | {
      id: string
      layout: "list" | "chips"
      options: QuizOption[]
      section: QuizSection
      type: "multi"
    }
  | { id: string; section: QuizSection; type: "statement" }
  | { id: string; section: QuizSection; type: "likert" }
  | { id: string; section: QuizSection; type: "dailyGoal" }
  | { id: string; section: QuizSection; type: "info"; visual: InfoVisual }
  | { id: string; section: QuizSection; type: "loader" }

export const QUIZ_STEPS: QuizStep[] = [
  {
    id: "gender",
    section: "aboutYou",
    type: "single",
    options: [
      { emoji: "👩", id: "female" },
      { emoji: "👨", id: "male" },
      { emoji: "🧑", id: "other" },
    ],
  },
  { id: "welcome", section: "aboutYou", type: "info", visual: "welcome" },
  {
    id: "learner-type",
    section: "aboutYou",
    type: "single",
    options: [
      { emoji: "🎓", id: "student" },
      { emoji: "💼", id: "professional" },
      { emoji: "🧭", id: "career-changer" },
      { emoji: "🔭", id: "self-learner" },
    ],
  },
  {
    id: "social-proof",
    section: "aboutYou",
    type: "info",
    visual: "socialProof",
  },
  {
    id: "main-goal",
    section: "aboutYou",
    type: "single",
    options: [
      { emoji: "📚", id: "exams" },
      { emoji: "🚀", id: "career" },
      { emoji: "🧠", id: "complex-topics" },
      { emoji: "🛠️", id: "new-skills" },
      { emoji: "✨", id: "curiosity" },
    ],
  },
  { id: "forget-quickly", section: "aboutYou", type: "statement" },
  { id: "hard-to-focus", section: "aboutYou", type: "statement" },
  {
    id: "forgetting-curve",
    section: "aboutYou",
    type: "info",
    visual: "forgettingCurve",
  },
  {
    id: "topics",
    layout: "chips",
    section: "goals",
    type: "multi",
    options: [
      {
        id: "ai-tech",
        subOptions: [
          { id: "ai-tools" },
          { id: "prompting" },
          { id: "automation" },
        ],
      },
      {
        id: "business",
        subOptions: [
          { id: "startups" },
          { id: "leadership" },
          { id: "strategy" },
        ],
      },
      {
        id: "science",
        subOptions: [{ id: "physics" }, { id: "biology" }, { id: "space" }],
      },
      {
        id: "psychology",
        subOptions: [
          { id: "habits" },
          { id: "emotions" },
          { id: "productivity" },
        ],
      },
      { id: "history" },
      { id: "finance" },
      { id: "languages" },
      { id: "health" },
      { id: "art-design" },
      {
        id: "programming",
        subOptions: [
          { id: "web-dev" },
          { id: "data-science" },
          { id: "python" },
        ],
      },
      { id: "marketing" },
      { id: "philosophy" },
    ],
  },
  {
    id: "sources",
    layout: "list",
    section: "goals",
    type: "multi",
    options: [
      { emoji: "▶️", id: "youtube" },
      { emoji: "🎒", id: "school-work" },
      { emoji: "📖", id: "books" },
      { emoji: "💡", id: "any-topic" },
    ],
  },
  {
    id: "mind-map-demo",
    section: "goals",
    type: "info",
    visual: "mindMapDemo",
  },
  { id: "see-connections", section: "goals", type: "likert" },
  { id: "overwhelmed", section: "goals", type: "statement" },
  { id: "learning-time", section: "learningStyle", type: "dailyGoal" },
  {
    id: "learning-moment",
    layout: "list",
    section: "learningStyle",
    type: "multi",
    options: [
      { emoji: "🌅", id: "morning" },
      { emoji: "🚇", id: "commute" },
      { emoji: "🥪", id: "lunch" },
      { emoji: "🌙", id: "evening" },
      { emoji: "🔀", id: "anytime" },
    ],
  },
  {
    id: "daily-habit",
    section: "learningStyle",
    type: "info",
    visual: "dailyHabit",
  },
  { id: "dont-stick", section: "learningStyle", type: "statement" },
  { id: "notes-messy", section: "learningStyle", type: "likert" },
  {
    id: "personalized",
    section: "learningStyle",
    type: "info",
    visual: "personalized",
  },
  { id: "creating-plan", section: "plan", type: "loader" },
]

export function getStepIndex(stepId: string): number {
  return QUIZ_STEPS.findIndex((step) => step.id === stepId)
}
